import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import ReactGA from "react-ga";
import { sendPageView } from "lib/googleAnalytics/page";
import log from "loglevel";
import { Container, Row, Col } from "react-bootstrap";
import { useWindowSize } from "lib/hooks";
import {
  decodeJwtAction,
  getAccountsAction,
  getAnalysesAction,
  getTeamsAction,
  getCurrentUserAccountOwnerAction,
} from "store/actions/actionCreators";
import DashboardSidebar from "components/sidebar/DashboardSidebar";
import MainNav from "components/nav/MainNav";
import AccountsCard from "components/dashboard/account/AccountsCard";
import AnalysesCard from "components/dashboard/analysis/AnalysesCard";
import TeamsCard from "components/dashboard/team/TeamsCard";
import MainPageMobileCards from "components/dashboard/MainPageMobileCards";
import {
  getAccountsReducerResponse,
  getAnalysesReducerResponse,
  getTeamsReducerResponse,
  getJwtReducerResponse,
  getIsCurrentUserAccountOwnerReducerResponse,
} from "store/selectors";
import {
  Account,
  Analysis,
  Team,
  JWT,
  CurrentUserAccountOwnerStatus,
} from "types";
import { administratorRoleId } from "settings";
import * as routes from "routes";
import {
  InfoCard,
  InfoTableCard,
  Breadcrumb,
  BreadcrumbData,
  Page,
  alenzaAccounts,
  alenzaStats,
  alenzaUsers,
} from "@avlino-inc/alenza-react";
import { faUsers } from "@fortawesome/pro-light-svg-icons/faUsers";
import { faAnalytics } from "@fortawesome/pro-light-svg-icons/faAnalytics";
import { faFolders } from "@fortawesome/pro-light-svg-icons/faFolders";
import dashboardPageStyles from "./DashboardPage.module.scss";

const DashboardMainPage = ({ authed }: { authed: Promise<boolean> }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const windowSize = useWindowSize();

  const accountsData: Account[] = useSelector((state) =>
    getAccountsReducerResponse(state, "data")
  );
  const analysesData: Analysis[] = useSelector((state) =>
    getAnalysesReducerResponse(state, "data")
  );
  const teamsData: Team[] = useSelector((state) =>
    getTeamsReducerResponse(state, "data")
  );
  const jwtPayloadData: JWT = useSelector((state) =>
    getJwtReducerResponse(state, "data")
  );
  const isCurrentUserAccountOwnerData: CurrentUserAccountOwnerStatus = useSelector(
    (state) => getIsCurrentUserAccountOwnerReducerResponse(state, "data")
  );

  const isAdmin = (jwtPayload: JWT, validRoleId: number) => {
    return jwtPayload && jwtPayload.role_id === validRoleId;
  };

  const isAvlino = (jwtPayload: JWT) => {
	return jwtPayload?.email?.endsWith('@avlino.com')
  }

  const isAccountOwner = (ownerData: CurrentUserAccountOwnerStatus) => {
    return ownerData && ownerData.is_owner;
  };

  useEffect(() => {
    if (authed) {
      authed.then((allow) => {
        log.info("Allow: " + allow);
        if (allow) {
          log.info("Authenticated. Stay on page...");
        } else {
          log.info("Redirect back...");
          history.push(routes.LOGIN);
        }
      });
    } else {
      history.push(routes.LOGIN);
    }
  }, [authed, history]);

  useEffect(() => {
    log.info("DashboardMainPage useEffect()...");
    dispatch(decodeJwtAction({}));
    dispatch(getAccountsAction({}));
    dispatch(getAnalysesAction({}));
    dispatch(getTeamsAction({}));
    dispatch(getCurrentUserAccountOwnerAction({}));
  }, [dispatch]);

  /**
   * Google Analytics
   */
  useEffect(() => {
    sendPageView(location.pathname + location.search);
  }, [location]);

  const breadcrumbItems = useMemo<BreadcrumbData[]>(
    () => [
      {
        url: routes.HOME,
        label: "Command Center",
      },
      {
        url: routes.DASHBOARD_MAIN,
        label: "Dashboard",
      },
    ],
    []
  );

  const [showNewAccountModal, setShowNewAccountModal] = useState(false);
  const [showNewAnalysisModal, setShowNewAnalysisModal] = useState(false);
  const [showNewTeamModal, setShowNewTeamModal] = useState(false);
  const [showAccountsSearch, setShowAccountsSearch] = useState(false);
  const [showAnalysesSearch, setShowAnalysesSearch] = useState(false);
  const [showTeamsSearch, setShowTeamsSearch] = useState(false);

  return (
    <div id="DashboardMainPage">
      <MainNav
        first_name={jwtPayloadData && jwtPayloadData.first_name}
        last_name={jwtPayloadData && jwtPayloadData.last_name}
        username={jwtPayloadData && jwtPayloadData.username}
        email={jwtPayloadData && jwtPayloadData.email}
      />
      <div style={{ display: "flex" }}>
        <DashboardSidebar
          isAvlino={isAvlino(jwtPayloadData)}
          isAdmin={isAdmin(jwtPayloadData, administratorRoleId)}
        />
        <main id="page-wrap">
          <Container fluid={true} style={{ marginBottom: "100px" }}>
            <Page.Header>
              <Breadcrumb items={breadcrumbItems} onClickItem={history.push} />
            </Page.Header>
            <Row>
              {windowSize.width <= 450 ? <MainPageMobileCards /> : null}
              <Col xs={12} xl={4}>
                <InfoCard
                  label="Accounts"
                  value={accountsData?.length || 0}
                  color="bright-blue"
                  icon={alenzaAccounts}
                />
                <br />
                <div className={dashboardPageStyles["info-table-card"]}>
                  <InfoTableCard
                    icon={alenzaAccounts}
                    color="bright-blue"
                    onNewClick={
                      isAdmin(jwtPayloadData, administratorRoleId)
                        ? () => {
                            setShowNewAccountModal(!showNewAccountModal);
                          }
                        : undefined
                    }
                    onMenuClick={() =>
                      showAccountsSearch
                        ? setShowAccountsSearch(false)
                        : setShowAccountsSearch(true)
                    }
                  >
                    <AccountsCard
                      showCreateModal={showNewAccountModal}
                      setShowCreateModal={setShowNewAccountModal}
                      accounts={accountsData}
                      userIsAdmin={isAdmin(jwtPayloadData, administratorRoleId)}
                      showSearch={showAccountsSearch}
                    />
                  </InfoTableCard>
                </div>
                <br />
              </Col>
              <Col xs={12} xl={4}>
                <InfoCard
                  label="Analyses"
                  value={analysesData?.length || 0}
                  color="ketchup"
                  icon={alenzaStats}
                />
                <br />
                <div className={dashboardPageStyles["info-table-card"]}>
                  <InfoTableCard
                    icon={alenzaStats}
                    color="ketchup"
                    onNewClick={
                      isAdmin(jwtPayloadData, administratorRoleId)
                        ? () => setShowNewAnalysisModal(!showNewAnalysisModal)
                        : undefined
                    }
                    onMenuClick={() =>
                      showAnalysesSearch
                        ? setShowAnalysesSearch(false)
                        : setShowAnalysesSearch(true)
                    }
                  >
                    <AnalysesCard
                      showCreateModal={showNewAnalysisModal}
                      setShowCreateModal={setShowNewAnalysisModal}
                      analyses={analysesData}
                      accounts={accountsData}
                      userIsAdmin={isAdmin(jwtPayloadData, administratorRoleId)}
                      showSearch={showAnalysesSearch}
                    />
                  </InfoTableCard>
                </div>
                <br />
              </Col>
              <Col xs={12} xl={4}>
                <InfoCard
                  label="Teams"
                  value={teamsData ? teamsData.length : 0}
                  color="eggplant"
                  icon={alenzaUsers}
                />
                <br />
                <div className={dashboardPageStyles["info-table-card"]}>
                  <InfoTableCard
                    icon={alenzaUsers}
                    color="eggplant"
                    onNewClick={
                      isAdmin(jwtPayloadData, administratorRoleId) ||
                      isAccountOwner(isCurrentUserAccountOwnerData)
                        ? () => setShowNewTeamModal(!showNewAnalysisModal)
                        : undefined
                    }
                    onMenuClick={() =>
                      showTeamsSearch
                        ? setShowTeamsSearch(false)
                        : setShowTeamsSearch(true)
                    }
                  >
                    <TeamsCard
                      showCreateModal={showNewTeamModal}
                      setShowCreateModal={setShowNewTeamModal}
                      teams={teamsData}
                      accounts={accountsData}
                      userIsAdmin={isAdmin(jwtPayloadData, administratorRoleId)}
                      userIsAccountOwner={isAccountOwner(
                        isCurrentUserAccountOwnerData
                      )}
                      showSearch={showTeamsSearch}
                    />
                  </InfoTableCard>
                </div>
                <br />
              </Col>
            </Row>
          </Container>
        </main>
      </div>
    </div>
  );
};

export default DashboardMainPage;
